/**
 * Modal Component
 * Reusable modal dialog with Alpine.js
 */

import Alpine from 'alpinejs'

// ============================================================================
// Types
// ============================================================================

export interface ModalData {
  open: boolean
  title: string
  content: string
  size: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  closable: boolean
  onClose?: () => void
  onConfirm?: () => void | Promise<void>
}

export interface ConfirmModalData extends ModalData {
  confirmText: string
  cancelText: string
  confirmClass: string
  loading: boolean
}

// ============================================================================
// Base Modal Component
// ============================================================================

/**
 * Create a modal component
 */
export function modal(initialData: Partial<ModalData> = {}) {
  return {
    open: initialData.open ?? false,
    title: initialData.title ?? '',
    content: initialData.content ?? '',
    size: initialData.size ?? 'md',
    closable: initialData.closable ?? true,

    /**
     * Show the modal
     */
    show(title?: string, content?: string): void {
      if (title) {
        this.title = title
      }
      if (content) {
        this.content = content
      }
      this.open = true

      // Prevent body scroll
      document.body.style.overflow = 'hidden'
    },

    /**
     * Hide the modal
     */
    hide(): void {
      this.open = false
      document.body.style.overflow = ''

      if (initialData.onClose) {
        initialData.onClose()
      }
    },

    /**
     * Close on escape key
     */
    handleKeydown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && this.closable) {
        this.hide()
      }
    },

    /**
     * Close on backdrop click
     */
    handleBackdropClick(event: MouseEvent): void {
      if (event.target === event.currentTarget && this.closable) {
        this.hide()
      }
    },

    /**
     * Get size classes
     */
    get sizeClasses(): string {
      const sizes: Record<string, string> = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        full: 'max-w-full mx-4',
      }
      return sizes[this.size] ?? sizes.md
    },
  }
}

// ============================================================================
// Confirm Modal Component
// ============================================================================

/**
 * Create a confirmation modal component
 */
export function confirmModal(initialData: Partial<ConfirmModalData> = {}) {
  const base = modal(initialData)

  return {
    ...base,
    confirmText: initialData.confirmText ?? 'Confirm',
    cancelText: initialData.cancelText ?? 'Cancel',
    confirmClass: initialData.confirmClass ?? 'btn-primary',
    loading: false,

    /**
     * Handle confirm action
     */
    async confirm(): Promise<void> {
      if (initialData.onConfirm) {
        this.loading = true
        try {
          await initialData.onConfirm()
          this.hide()
        } catch (error) {
          console.error('Confirm action failed:', error)
        } finally {
          this.loading = false
        }
      } else {
        this.hide()
      }
    },

    /**
     * Handle cancel action
     */
    cancel(): void {
      this.hide()
    },
  }
}

// ============================================================================
// Image Preview Modal Component
// ============================================================================

/**
 * Create an image preview modal component
 */
export function imageModal() {
  return {
    open: false,
    imageUrl: '',
    alt: '',
    loading: true,

    /**
     * Show image in modal
     */
    show(url: string, altText = 'Image preview'): void {
      this.imageUrl = url
      this.alt = altText
      this.loading = true
      this.open = true
      document.body.style.overflow = 'hidden'
    },

    /**
     * Hide modal
     */
    hide(): void {
      this.open = false
      this.imageUrl = ''
      document.body.style.overflow = ''
    },

    /**
     * Handle image load
     */
    onImageLoad(): void {
      this.loading = false
    },

    /**
     * Handle image error
     */
    onImageError(): void {
      this.loading = false
    },
  }
}

// ============================================================================
// Report Detail Modal Component
// ============================================================================

/**
 * Create a report detail modal component
 */
export function reportDetailModal() {
  return {
    open: false,
    report: null as unknown,
    loading: false,

    /**
     * Show report details
     */
    show(report: unknown): void {
      this.report = report
      this.open = true
      document.body.style.overflow = 'hidden'
    },

    /**
     * Hide modal
     */
    hide(): void {
      this.open = false
      this.report = null
      document.body.style.overflow = ''
    },

    /**
     * Handle escape key
     */
    handleKeydown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        this.hide()
      }
    },
  }
}

// ============================================================================
// Register Components
// ============================================================================

export function registerModalComponents(): void {
  Alpine.data('modal', modal)
  Alpine.data('confirmModal', confirmModal)
  Alpine.data('imageModal', imageModal)
  Alpine.data('reportDetailModal', reportDetailModal)
}
